import { accessTokenSchema, type AccessToken } from '@/models/api/access-token'
import type { AnalyzeTokenByInvitationRequest } from '@/models/api/analyze-token-by-invitation-request'
import {
  corporateAccountUserProfileSchema,
  type CorporateAccountUserProfile
} from '@/models/api/corporate-account-user-profile'
import type { CorporateLoginData } from '@/models/api/corporate-login-data'
import {
  loginAnalyzeSchema,
  type LoginAnalyze
} from '@/models/api/login-analyze'
import type { LoginByLoginKeyRequest } from '@/models/api/login-by-login-key-request'
import type { LoginData } from '@/models/api/login-data'
import {
  loginDecryptFfpMemberSchema,
  type LoginDecryptFfpMember
} from '@/models/api/login-decrypt-ffp-member'
import type { LoginDecryptFfpMemberRequest } from '@/models/api/login-decrypt-ffp-member-request'
import type { LoginGetCaptcha } from '@/models/api/login-get-captcha'
import { loginGetCaptchaSchema } from '@/models/api/login-get-captcha'
import type { OtpGetRemainingTime } from '@/models/api/otp-get-remaining-time'
import type { OtpLoginToken } from '@/models/api/otp-login-token'
import { type OtpVerificationData } from '@/models/api/otp-verification-data'
import {
  basicUserProfileSchema,
  userProfileSchema,
  type BasicUserProfile,
  type UserProfile
} from '@/models/api/user-profile'
import type { RefreshToken } from '~library/models'
import {
  apiValidator,
  type ApiFactory,
  type ApiRequestConfig,
  type ApiResponse
} from '~library/services'

export default class LoginApi {
  constructor(private api: ApiFactory) {}

  /**
   * API 解析邀請信 Token
   * https://hackmd.io/uQf0lOMMQRibeH8O1ujBdQ
   */
  @apiValidator(loginAnalyzeSchema)
  analyzeTokenByInvitation(
    data: AnalyzeTokenByInvitationRequest
  ): Promise<ApiResponse<LoginAnalyze>> {
    return this.api.post('Login/AnalyzeTokenByInvitation', data, {
      headers: {
        ignoreRtnCodeError: ['95', '96']
      }
    })
  }

  /**
   * API 會員登入
   * https://hackmd.io/cWWrGy2FTr2WeXu5Obgb0w
   */
  @apiValidator(userProfileSchema.nullable())
  login(data: LoginData): Promise<ApiResponse<UserProfile | null>> {
    return this.api.post('Login/Login', data, {
      headers: {
        ignoreRtnCodeError: ['10', '46', '97']
      }
    })
  }

  /**
   * API 更新API Token
   * https://hackmd.io/K3Eh51w1T2OZPYTXpOr6nQ
   */
  @apiValidator(accessTokenSchema.nullable())
  refreshToken(data: RefreshToken): Promise<ApiResponse<AccessToken | null>> {
    return this.api.post('Login/RefreshToken', data)
  }

  /**
   * API 取得登入驗證碼
   * https://hackmd.io/nc9Y7VVUQCaiqvAXmgjRLQ
   */
  @apiValidator(loginGetCaptchaSchema)
  getCaptcha(
    number: number | string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<LoginGetCaptcha>> {
    return this.api.get(`Login/GetCaptcha/${number}`, config)
  }

  /**
   * API 解密FFP會員資料
   * https://hackmd.io/-i9EIjwxS_S-RfmDaiT-LQ
   */
  @apiValidator(loginDecryptFfpMemberSchema)
  decryptFfpMember(
    data: LoginDecryptFfpMemberRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<LoginDecryptFfpMember>> {
    return this.api.post('Login/DecryptFfpMember', data, config)
  }

  /**
   * API 企業帳號登入
   * https://hackmd.io/Lgc04sfEToOzFWBdmukeVw
   */
  @apiValidator(corporateAccountUserProfileSchema.nullable())
  corporateLogin(
    data: CorporateLoginData
  ): Promise<ApiResponse<CorporateAccountUserProfile | null>> {
    return this.api.post('Login/CorporateLogin', data, {
      headers: {
        ignoreRtnCodeError: ['4', '10', '46', '47', '48', '54', '55', '97']
      }
    })
  }

  /**
   * API 會員OTP驗證
   * https://hackmd.io/vQxvdjVCQ_m4n7awbxtb-Q
   */
  oTPVerification(
    data: OtpVerificationData
  ): Promise<ApiResponse<OtpLoginToken>> {
    return this.api.post('Login/OTPVerification', data, {
      headers: {
        ignoreRtnCodeError: ['66', '67']
      }
    })
  }

  /**
   * API 取得會員OTP時間
   * https://hackmd.io/WgrCh95hTvuPbspbbrbzjg
   */
  getOTPExpireTime(data: {
    sseCode: string
  }): Promise<ApiResponse<OtpGetRemainingTime>> {
    return this.api.post('Login/GetOTPExpireTime', data, {
      headers: {
        ignoreRtnCodeError: ['66', '67']
      }
    })
  }

  /**
   * API 使用登入金鑰登入 (SSO登入)
   * https://hackmd.io/9inbBc-QS7qwXZtAUQhhfg
   */
  @apiValidator(basicUserProfileSchema.nullable())
  loginByLoginKey(
    data: LoginByLoginKeyRequest
  ): Promise<ApiResponse<BasicUserProfile | null>> {
    return this.api.post('Login/LoginByLoginKey', data)
  }
}
